<template>
  <div class="checkout-page repay-checkout">
    <van-nav-bar :fixed="true" z-index="9" :title="$t('planDetail.payRentalBill')" :border="false" left-arrow @click-left="onClickLeft" />

    <div v-if="loading || !initialLoadDone" class="checkout-page__state">{{ $t('loading') }}</div>
    <div v-else-if="loadError" class="checkout-page__state checkout-page__state--error">
      <div>{{ $t('systemError') }}</div>
      <button type="button" @click="loadPage">{{ $t('retry') }}</button>
    </div>
    <template v-else>
      <div class="checkout-page__safe">
        <div class="safe-strip-icon" aria-hidden="true">
          <span class="safe-strip-icon__shield"></span>
        </div>
        <span>{{ $t('orderPage.transaksiAman') }}</span>
      </div>

      <section class="checkout-page__amount-card">
        <div>
          <div class="amount-card__label">{{ $t('planDetail.payRentalBill') }}</div>
          <div class="amount-card__value">{{ checkoutPriceUnit }} {{ repayAmount | numberFormat }}</div>
        </div>
        <div class="receipt-illustration">
          <div class="receipt-illustration__paper">
            <span></span>
            <span></span>
            <span></span>
          </div>
          <div class="receipt-illustration__coin">Rp</div>
        </div>
        <div class="amount-card__divider"></div>
        <div
          class="section-title amount-card__detail-toggle"
          :class="{ 'amount-card__detail-toggle--expanded': detailExpanded }"
          @click="detailExpanded = !detailExpanded"
        >
          <span>{{ $t('planDetail.billDetails') }}</span>
          <van-icon name="arrow-down" />
        </div>
        <transition name="details-expand">
          <div v-if="detailExpanded" class="details-body amount-card__details-body">
            <div class="detail-row">
              <span>{{ $t('planDetail.rentalPeriodCount') }}</span>
              <span>{{ selectedPeriodsText }}</span>
            </div>
            <div class="detail-row">
              <span>{{ $t('planDetail.totalAmountDue') }}</span>
              <span>{{ checkoutPriceUnit }}{{ repayAmount | numberFormat }}</span>
            </div>
            <div class="detail-row">
              <span>{{ $t('payment.orderNumber') }}</span>
              <span>{{ orderId }}</span>
            </div>
          </div>
        </transition>
      </section>

      <section class="checkout-page__payment-section">
        <div class="section-heading">{{ $t('orderPage.choosePaymentMethod') }}</div>
        <PayMethodSelector
          :value.sync="selectedPayMethod"
          :cod-enabled="false"
          :online-methods="onlinePayMethods"
          :disabled-methods="disabledOnlinePayMethods"
        />
      </section>

      <div class="checkout-page__safe-card">
        <div class="safe-card__icon"><van-icon name="shield-o" /></div>
        <div>
          <div class="safe-card__title">{{ $t('orderPage.belanjaAmanTitle') }}</div>
          <div class="safe-card__desc">{{ safeCardDesc }}</div>
        </div>
      </div>
    </template>

    <div v-if="!loadError && initialLoadDone" class="checkout-page__footer">
      <div class="footer-total">
        <div class="footer-total__label">{{ $t('planDetail.totalAmountDue') }}</div>
        <div class="footer-total__value">{{ checkoutPriceUnit }} {{ repayAmount | numberFormat }}</div>
      </div>
      <div class="checkout-btn" :class="{ 'btn-disabled': submitting || !canPayRepay }" @click="submitPayment">
        <van-icon name="lock" />
        <span>{{ submitting ? $t('loading') : $t('planDetail.payRentalBill') }}</span>
      </div>
    </div>

    <PaymentPopup ref="paymentPopup" @close="onPaymentPopupClose" />
    <van-popup v-model="showPayUrlPopup" position="bottom">
      <div class="pay-url-popup">
        <div class="pay-url-popup__close" @click="showPayUrlPopup = false">
          <img src="@/assets/img/close.png" alt="">
        </div>
        <div class="pay-url-popup__title">{{ $t('orderPage.payUrlTitle') }}</div>
        <div class="pay-url-popup__desc">{{ $t('orderPage.payUrlDesc') }}</div>
        <div class="pay-url-popup__footer">
          <button type="button" class="pay-url-popup__primary" @click="goToPayUrl">{{ $t('orderPage.goToPay') }}</button>
          <button
            type="button"
            class="pay-url-popup__completed"
            :disabled="checkingPaymentStatus"
            @click="checkRepayPaymentStatus"
          >
            {{ $t('orderPage.paymentCompletedButton') }}
          </button>
        </div>
      </div>
    </van-popup>
  </div>
</template>

<script>
import { Icon, Popup } from 'vant';
import { mapState } from 'vuex';
import PayMethodSelector from '@/components/PayMethodSelector.vue';
import PaymentPopup from '@/components/PaymentPopup/index.vue';
import { getAppSystemConfig } from '@/api/common';
import { queryOrderDetail, repay } from '@/api/order';
import { queryPaymentInfo } from '@/api/payment';
import { PAYMENT_STATUS } from '@/common/constants';
import { DEFAULT_REG_CHANNEL } from '@/config';

const REPAY_THIRD_PARTY_RETURN_QUERY = 'fromThirdParty';
const REPAY_CHECKOUT_SOURCE_QUERY = 'sourcePage';
const ONLINE_PAY_CHANNELS = {
  ayolinx: { titleKey: 'orderPage.onlinePayQrisTitle', descKey: 'orderPage.onlinePayQrisDesc', iconType: 'qris', tags: [] },
  ayolinx_paylink: { titleKey: 'orderPage.onlinePayAggregatedTitle', descKey: 'orderPage.onlinePayAggregatedDesc', iconType: 'wallet', tags: ['VA', 'E-wallet', 'QRIS'] },
  paycools: { titleKey: 'orderPage.onlinePayAggregatedTitle', descKey: 'orderPage.onlinePayAggregatedDesc', iconType: 'wallet', tags: ['VA', 'E-wallet', 'QRIS'] }
};

export default {
  name: 'RepayCheckout',
  components: {
    PayMethodSelector,
    PaymentPopup,
    'van-icon': Icon,
    [Popup.name]: Popup
  },
  data() {
    return {
      selectedPayMethod: '',
      onlinePayMethods: [],
      systemConfigLoaded: false,
      orderId: '',
      orderDetail: null,
      loading: true,
      initialLoadDone: false,
      loadError: false,
      submitting: false,
      detailExpanded: true,
      checkingPaymentStatus: false,
      showPayUrlPopup: false,
      activePayUrl: '',
      pendingPayment: null,
      pendingRepayOrderId: '',
      sourcePage: '',
      resumeFromThirdParty: false,
      pollingTimer: null,
      pollingAttempts: 0,
      maxPollingAttempts: 60,
      skipNextPaymentPopupCloseRefresh: false,
      skipNextPayUrlPopupCloseRefresh: false
    };
  },
  computed: {
    ...mapState({
      channel: state => state.user.channel || DEFAULT_REG_CHANNEL
    }),
    priceUnit() {
      return this.$t('priceUnit');
    },
    safeCardDesc() {
      return this.$t('orderPage.belanjaAmanDesc');
    },
    checkoutPriceUnit() {
      return String(this.priceUnit || '').toUpperCase();
    },
    repayAmount() {
      const pendingAmount = Number(this.pendingPayment?.amount || 0);
      const routeAmount = Number(this.$route.query.amount || 0);
      return pendingAmount > 0 ? pendingAmount : routeAmount;
    },
    selectedPeriods() {
      return String(this.$route.query.periods || '')
        .split(',')
        .map(period => Number(period))
        .filter(period => period > 0);
    },
    selectedPeriodsText() {
      if (!this.selectedPeriods.length) return '-';
      return this.selectedPeriods.join(', ');
    },
    pendingPaymentChannel() {
      return String(
        this.pendingPayment?.payChannel ||
        this.pendingPayment?.payChannelAPI ||
        this.pendingPayment?.payMethod ||
        ''
      ).trim();
    },
    disabledOnlinePayMethods() {
      if (!this.pendingPaymentChannel) return [];
      return this.onlinePayMethods
        .filter(item => item.key !== this.pendingPaymentChannel)
        .map(item => item.key);
    },
    effectivePayChannel() {
      if (this.pendingPaymentChannel) return this.pendingPaymentChannel;
      return this.isValidOnlinePayMethod(this.selectedPayMethod) ? this.selectedPayMethod : '';
    },
    canPayRepay() {
      return this.repayAmount > 0;
    }
  },
  watch: {
    showPayUrlPopup(show) {
      if (!show) {
        this.activePayUrl = '';
        if (this.skipNextPayUrlPopupCloseRefresh) {
          this.skipNextPayUrlPopupCloseRefresh = false;
          return;
        }
        this.refreshPaymentStateAfterPopupClose();
      }
    }
  },
  async mounted() {
    this.orderId = this.$route.query.orderId || '';
    if (!this.orderId) {
      this.$router.replace({ name: 'RentalplanList' });
      return;
    }
    this.scrollToTop();
    this.sourcePage = String(this.$route.query[REPAY_CHECKOUT_SOURCE_QUERY] || '').trim();
    if (this.$route.query.returnPath) {
      const query = { ...this.$route.query };
      delete query.returnPath;
      this.$router.replace({
        path: this.$route.path,
        query
      });
    }
    this.resumeFromThirdParty = String(this.$route.query[REPAY_THIRD_PARTY_RETURN_QUERY] || '') === '1';
    await Promise.all([
      this.loadOrderDetail(),
      this.loadSystemFeatures()
    ]);
    this.syncSelectedPayMethod();
    if (this.resumeFromThirdParty) {
      this.resumePaymentFromThirdParty();
    }
  },
  beforeDestroy() {
    this.stopPolling();
  },
  beforeRouteLeave(to, from, next) {
    this.stopPolling();
    next();
  },
  beforeRouteUpdate(to, from, next) {
    this.scrollToTop();
    next();
  },
  methods: {
    normalizeStatus(value = '') {
      return String(value == null ? '' : value).trim().toUpperCase();
    },
    onClickLeft() {
      this.goBackToSource();
    },
    goBackToSource() {
      if (this.sourcePage === 'detail') {
        this.$router.replace({
          name: 'RentalplanDetail',
          query: { orderId: this.orderId }
        });
        return;
      }
      if (this.sourcePage === 'list') {
        this.$router.replace({ name: 'RentalplanList' });
        return;
      }
      this.$router.replace({ name: 'RentalplanList' });
    },
    async loadPage() {
      this.loading = true;
      this.loadError = false;
      await Promise.all([
        this.loadOrderDetail(),
        this.loadSystemFeatures()
      ]);
      this.syncSelectedPayMethod();
    },
    async loadOrderDetail({ silent = false } = {}) {
      if (!silent) {
        this.loading = true;
      }
      this.loadError = false;
      try {
        const res = await queryOrderDetail({ orderId: this.orderId });
        if (res?.code === 'Success') {
          this.orderDetail = res.data || null;
        } else if (!this.orderDetail) {
          this.loadError = true;
        }
        return res;
      } catch (e) {
        console.error('Load repay checkout order failed:', e);
        if (!this.orderDetail) {
          this.loadError = true;
        }
        return null;
      } finally {
        this.loading = false;
        this.initialLoadDone = true;
      }
    },
    getDefaultOnlinePayMethod() {
      return this.onlinePayMethods[0]?.key || '';
    },
    scrollToTop() {
      this.$nextTick(() => {
        window.scrollTo(0, 0);
      });
    },
    getDefaultPayMethod() {
      return this.onlinePayMethods.length ? this.onlinePayMethods[0].key : '';
    },
    isValidOnlinePayMethod(value) {
      return !!value && this.onlinePayMethods.some(item => item.key === value);
    },
    restoreSelectedPayMethod() {
      const candidate = this.$route.query.payChannel || '';
      const payChannel = candidate.includes('?') ? candidate.split('?')[0] : candidate;
      if (this.isValidOnlinePayMethod(payChannel)) {
        this.selectedPayMethod = payChannel;
        return;
      }
      this.selectedPayMethod = this.getDefaultPayMethod();
    },
    syncSelectedPayMethod() {
      if (this.pendingPaymentChannel) {
        this.selectedPayMethod = this.pendingPaymentChannel;
        return;
      }
      if (this.isValidOnlinePayMethod(this.selectedPayMethod)) {
        return;
      }
      this.restoreSelectedPayMethod();
    },
    async loadSystemFeatures() {
      if (this.systemConfigLoaded) return;
      try {
        const res = await getAppSystemConfig({ platform: 'h5', channel: this.channel || DEFAULT_REG_CHANNEL });
        const payments = res?.data?.features?.payments || [];
        const methods = (Array.isArray(payments) ? payments : [])
          .map(key => {
            const config = ONLINE_PAY_CHANNELS[key];
            if (!config) return null;
            return {
              key,
              title: this.$t(config.titleKey),
              desc: this.$t(config.descKey),
              iconType: config.iconType,
              tags: config.tags
            };
          })
          .filter(Boolean);
        this.onlinePayMethods = methods;
      } catch (e) {
        console.warn('Failed to load repay checkout payment methods:', e);
      } finally {
        this.systemConfigLoaded = true;
      }
    },
    async refreshPaymentStateAfterPopupClose() {
      await Promise.all([
        this.loadOrderDetail({ silent: true })
      ]);
      this.syncSelectedPayMethod();
    },
    buildReturnUrl() {
      if (typeof window === 'undefined' || !window.location) return '';
      const url = new URL(window.location.href);
      url.searchParams.delete('returnPath');
      const payChannel = this.pendingPaymentChannel ||
        (this.isValidOnlinePayMethod(this.selectedPayMethod) ? this.selectedPayMethod : '');
      if (payChannel) {
        url.searchParams.set('payChannel', payChannel);
      } else {
        url.searchParams.delete('payChannel');
      }
      url.searchParams.set(REPAY_THIRD_PARTY_RETURN_QUERY, '1');
      return url.toString();
    },
    clearThirdPartyReturnQuery() {
      const query = { ...this.$route.query };
      delete query[REPAY_THIRD_PARTY_RETURN_QUERY];
      this.$router.replace({
        path: this.$route.path,
        query
      });
    },
    resumePaymentFromThirdParty() {
      if (!this.resumeFromThirdParty) return;
      this.resumeFromThirdParty = false;
      this.clearThirdPartyReturnQuery();
      this.$nextTick(() => {
        this.submitPayment();
      });
    },
    async submitPayment() {
      if (this.submitting) return;
      if (!this.canPayRepay) {
        await this.loadOrderDetail({ silent: true });
        if (!this.canPayRepay) {
          this.$toast({ message: this.$t('systemError'), duration: 3000 });
          return;
        }
      }
      this.submitting = true;
      try {
        this.syncSelectedPayMethod();
        const res = await repay({
          rentalOrderId: this.orderId,
          periods: this.selectedPeriods,
          amount: this.repayAmount,
          returnUrl: this.buildReturnUrl(),
          payChannel: this.effectivePayChannel
        });
        if (res?.code !== 'Success') {
          this.$toast({ message: res?.message || this.$t('systemError'), duration: 3000 });
          return;
        }
        const payInfo = res.data || {};
        this.pendingPayment = payInfo;
        this.pendingRepayOrderId = String(
          payInfo.repayOrderId ||
          payInfo.payOrderId ||
          payInfo.orderId ||
          ''
        ).trim();
        if (this.normalizeStatus(payInfo.payStatus) === PAYMENT_STATUS.SUCCESS) {
          this.skipNextPayUrlPopupCloseRefresh = true;
          this.skipNextPaymentPopupCloseRefresh = true;
          this.$toast({ message: this.$t('payment.success'), duration: 2000 });
          this.showPayUrlPopup = false;
          if (this.$refs.paymentPopup) {
            this.$refs.paymentPopup.close();
          }
          this.goBackToSource();
          return;
        }
        if (String(payInfo.payContent || '').trim()) {
          this.$refs.paymentPopup.open({
            payContent: payInfo.payContent,
            amount: payInfo.amount || this.repayAmount
          });
          this.startPolling();
          return;
        }
        if (String(payInfo.payUrl || '').trim()) {
          this.activePayUrl = payInfo.payUrl;
          this.showPayUrlPopup = true;
          return;
        }
        this.$toast({ message: this.$t('systemError'), duration: 3000 });
      } catch (e) {
        console.error('Create repay payment failed:', e);
        this.$toast({ message: this.$t('systemError'), duration: 3000 });
      } finally {
        this.submitting = false;
      }
    },
    async onPaymentPopupClose() {
      this.stopPolling();
      if (this.skipNextPaymentPopupCloseRefresh) {
        this.skipNextPaymentPopupCloseRefresh = false;
        return;
      }
      await this.refreshPaymentStateAfterPopupClose();
    },
    startPolling() {
      this.stopPolling();
      this.pollingAttempts = 0;
      this.pollingTimer = setInterval(() => {
        this.pollingAttempts += 1;
        if (this.pollingAttempts > this.maxPollingAttempts) {
          this.stopPolling();
          return;
        }
        this.checkRepayPaymentStatus({ silentPending: true });
      }, 30000);
    },
    stopPolling() {
      if (this.pollingTimer) {
        clearInterval(this.pollingTimer);
        this.pollingTimer = null;
      }
    },
    goToPayUrl() {
      if (!this.activePayUrl) return;
      window.open(this.activePayUrl, '_blank', 'noopener,noreferrer');
    },
    async checkRepayPaymentStatus({ silentPending = false } = {}) {
      if (this.checkingPaymentStatus) return;
      this.checkingPaymentStatus = true;
      try {
        const queryOrderId = this.pendingRepayOrderId || this.pendingPayment?.repayOrderId || this.pendingPayment?.payOrderId || '';
        if (!queryOrderId) {
          if (!silentPending) {
            this.$toast({ message: this.$t('systemError'), duration: 3000 });
          }
          return;
        }
        const res = await queryPaymentInfo({ orderId: queryOrderId });
        if (res?.code !== 'Success') {
          if (!silentPending) {
            this.$toast({ message: res?.message || this.$t('systemError'), duration: 3000 });
          }
          return;
        }
        if (res.data?.state !== PAYMENT_STATUS.SUCCESS) {
          if (!silentPending) {
            this.$toast({ message: this.$t('orderPage.paymentNotCompleted'), duration: 3000 });
          }
          return;
        }

        this.stopPolling();
        this.skipNextPayUrlPopupCloseRefresh = true;
        this.skipNextPaymentPopupCloseRefresh = true;
        this.showPayUrlPopup = false;
        if (this.$refs.paymentPopup) {
          this.$refs.paymentPopup.close();
        }
        this.$toast({ message: this.$t('payment.success'), duration: 2000 });
        this.goBackToSource();
      } catch (e) {
        this.$toast({ message: this.$t('systemError'), duration: 3000 });
      } finally {
        this.checkingPaymentStatus = false;
      }
    }
  }
};
</script>

<style lang="less">
.pay-url-popup {
  position: relative;
  padding: 48px 40px 44px;
  box-sizing: border-box;
  text-align: center;

  &__close {
    position: absolute;
    top: 24px;
    right: 24px;
    width: 36px;
    height: 36px;

    img {
      display: block;
      width: 100%;
      height: 100%;
    }
  }

  &__title {
    color: #111827;
    font-size: 34px;
    line-height: 48px;
    font-weight: 700;
  }

  &__desc {
    margin-top: 20px;
    color: #6B7285;
    font-size: 26px;
    line-height: 38px;
  }

  &__footer {
    margin-top: 40px;
  }

  &__primary,
  &__completed {
    width: 100%;
    height: 80px;
    border-radius: 12px;
    box-sizing: border-box;
    font-size: 28px;
    line-height: 38px;
  }

  &__primary {
    border: 0;
    color: #FFFFFF;
    background: #2563EB;
  }

  &__completed {
    margin-top: 20px;
    border: 2px solid #2563EB;
    color: #2563EB;
    background: #FFFFFF;

    &:disabled {
      opacity: 0.5;
      pointer-events: none;
    }
  }
}
</style>

<style lang="less" scoped>
.checkout-page {
  min-height: 100vh;
  padding: 118px 30px 270px;
  box-sizing: border-box;
  background: #FFFFFF;

  ::v-deep .van-nav-bar {
    height: 108px;
    line-height: 108px;
    background: #FFFFFF;
  }

  ::v-deep .van-nav-bar__title {
    color: #0B0B12;
    font-size: 36px;
    font-weight: 700;
  }

  &__state {
    margin-top: 240px;
    text-align: center;
    color: #64748B;
    font-size: 28px;
    line-height: 40px;

    &--error {
      color: #0B0B12;

      button {
        margin-top: 24px;
        padding: 0 28px;
        height: 72px;
        border: 0;
        border-radius: 16px;
        background: #0B45FF;
        color: #FFFFFF;
        font-size: 26px;
        line-height: 72px;
      }
    }
  }

  &__safe {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 16px;
    margin-top: 12px;
    height: 64px;
    padding: 0 32px;
    border-radius: 22px;
    background: linear-gradient(100deg, #F4FCF8 0%, #EFF8F4 100%);
    color: #2FA76F;
    font-size: 28px;
    line-height: 32px;
    font-weight: 600;
    box-sizing: border-box;
  }

  &__amount-card {
    position: relative;
    margin-top: 24px;
    padding: 30px 38px 0;
    border: 1px solid #EEF0F5;
    border-radius: 24px;
    background: #FFFFFF;
    box-shadow: 0 12px 30px rgba(24, 37, 72, 0.10);
    overflow: hidden;
    box-sizing: border-box;
  }

  &__payment-section {
    margin-top: 28px;
  }

  &__safe-card {
    display: flex;
    align-items: center;
    gap: 22px;
    min-height: 100px;
    margin-top: 24px;
    padding: 20px 30px;
    border: 0;
    border-radius: 18px;
    background: linear-gradient(100deg, #F2F7FF 0%, #FAFCFF 100%);
    box-shadow: none;
    box-sizing: border-box;
  }

  &__footer {
    position: fixed;
    left: 0;
    right: 0;
    bottom: 0;
    display: flex;
    flex-direction: column;
    align-items: stretch;
    gap: 16px;
    padding: 24px 30px 36px;
    background: #FFFFFF;
    box-shadow: 0 -8px 30px rgba(15, 23, 42, 0.10);
    box-sizing: border-box;
  }
}

.section-title,
.section-heading {
  display: flex;
  align-items: center;
  justify-content: space-between;
  font-size: 28px;
  line-height: 36px;
  font-weight: 600;
  color: #1D2437;

  .van-icon {
    font-size: 34px;
    color: #0B1635;
  }
}

.section-heading {
  margin-bottom: 20px;
  color: #70758C;
  text-transform: none;
}

.safe-strip-icon {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: 28px;
  height: 32px;

  &__shield {
    position: relative;
    width: 25px;
    height: 29px;
    border-radius: 13px 13px 10px 10px;
    background: #28A96B;
    clip-path: polygon(50% 0, 92% 14%, 84% 72%, 50% 100%, 16% 72%, 8% 14%);

    &::after {
      position: absolute;
      content: '';
      left: 7px;
      top: 10px;
      width: 10px;
      height: 6px;
      border-left: 3px solid #FFFFFF;
      border-bottom: 3px solid #FFFFFF;
      transform: rotate(-45deg);
    }
  }
}

.amount-card {
  position: relative;
  min-height: 238px;
  padding: 30px 38px 0;
  box-sizing: border-box;

  &__label {
    color: #6B7285;
    font-size: 28px;
    line-height: 36px;
    font-weight: 600;
  }

  &__value {
    margin-top: 16px;
    color: #0B45FF;
    font-size: 54px;
    line-height: 62px;
    font-weight: 400;
    white-space: nowrap;
  }

  &__divider {
    margin-top: 28px;
    border-top: 1px solid #E3E6ED;
  }

  &__detail-toggle {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    height: 78px;
    color: #5E6478;
    font-size: 26px;
    line-height: 34px;

    .van-icon {
      transition: transform .2s ease;
    }

    &--expanded .van-icon {
      transform: rotate(180deg);
    }
  }

  &__details-body {
    margin: 0 0 30px;
    padding-top: 4px;
    transform-origin: top center;
  }
}

.receipt-illustration {
  position: absolute;
  right: 42px;
  top: 30px;
  width: 170px;
  height: 150px;

  &__paper {
    position: absolute;
    right: 36px;
    top: 0;
    width: 104px;
    height: 126px;
    border: 8px solid rgba(37, 101, 242, 0.60);
    border-radius: 12px;
    background: linear-gradient(180deg, rgba(243, 248, 255, 0.90), rgba(255, 255, 255, 0.85));

    &::before,
    &::after {
      position: absolute;
      content: '';
      top: -8px;
      width: 18px;
      height: 18px;
      border-right: 6px solid rgba(37, 101, 242, 0.60);
      border-bottom: 6px solid rgba(37, 101, 242, 0.60);
      border-left: 6px solid rgba(37, 101, 242, 0.60);
      border-radius: 0 0 10px 10px;
      background: #FFFFFF;
    }

    &::before { left: 28px; }
    &::after { right: 20px; }

    span {
      display: block;
      width: 46px;
      height: 8px;
      margin: 28px auto 0;
      border-radius: 8px;
      background: rgba(37, 101, 242, 0.48);

      & + span {
        margin-top: 14px;
      }
    }
  }

  &__coin {
    position: absolute;
    right: 0;
    top: 52px;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 76px;
    height: 76px;
    border-radius: 50%;
    background: linear-gradient(135deg, #5AA2FF, #1259F3);
    color: #FFFFFF;
    font-size: 30px;
    font-weight: 700;
    box-shadow: 0 10px 20px rgba(18, 89, 243, 0.28);
  }
}

.details-expand-enter-active,
.details-expand-leave-active {
  max-height: 720px;
  opacity: 1;
  overflow: hidden;
  transform: translateY(0);
  transition: max-height 0.32s cubic-bezier(0.22, 1, 0.36, 1), opacity 0.22s ease, transform 0.32s ease, padding 0.32s ease, margin 0.32s ease;
  will-change: max-height, opacity, transform;
}

.details-expand-enter,
.details-expand-leave-to {
  max-height: 0;
  opacity: 0;
  margin-bottom: 0;
  padding-top: 0;
  transform: translateY(-8px);
}

.details-body {
  margin-top: 20px;
}

.detail-row {
  display: flex;
  justify-content: space-between;
  gap: 16px;
  font-size: 26px;
  line-height: 36px;
  color: #6B7285;

  & + & {
    margin-top: 16px;
  }

  span:last-child {
    color: #020B3A;
    text-align: right;
    font-weight: 400;
  }

  &--strong {
    span:last-child {
      color: #0B45FF;
      font-weight: 400;
    }
  }
}

.safe-card {
  &__icon {
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    width: 58px;
    height: 58px;
    border-radius: 50%;
    background: #E6F0FF;
    color: #1C63F2;
    font-size: 34px;
  }

  &__title {
    font-size: 28px;
    line-height: 36px;
    font-weight: 700;
    color: #0F1F45;
  }

  &__desc {
    margin-top: 8px;
    font-size: 24px;
    line-height: 32px;
    color: #6B7285;
  }
}

.checkout-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 18px;
  width: 100%;
  height: 88px;
  border: 0;
  border-radius: 14px;
  background: #1259F3;
  color: #FFFFFF;
  font-size: 32px;
  line-height: 40px;
  font-weight: 700;

  .van-icon {
    font-size: 34px;
  }

  &.btn-disabled {
    opacity: 0.5;
  }
}

.footer-total {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  flex: 0 1 auto;
  min-width: 0;
  color: #0B0B12;

  &__label {
    overflow: hidden;
    font-size: 26px;
    line-height: 34px;
    color: #5E6478;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  &__value {
    margin-top: 4px;
    overflow: hidden;
    font-size: 38px;
    line-height: 44px;
    font-weight: 400;
    color: #0B45FF;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
}
</style>
